import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Users, Star } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHero } from "@/components/site/PageBits";
import { JobForm } from "@/components/jobs/JobForm";
import { JobCard, MyPosts, MyApplied } from "@/components/jobs/JobParts";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useSeo, pageSeo } from "@/lib/seo";

const SEO = pageSeo("/jobs");

function SignInPrompt({ what }) {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-[#0B2A4A]/20 p-8 text-center" data-testid="jobs-sign-in-prompt">
      <p className="text-slate-700">Sign in or create a free account to {what}.</p>
      <Link to="/login?next=/jobs" className="mt-4 inline-block rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-5 py-3 font-semibold" data-testid="jobs-sign-in-btn">Sign in / Create account</Link>
    </div>
  );
}

function Browse({ data }) {
  if (!data) return null;
  return (
    <div className="flex flex-col gap-4">
      {!data.is_member && <p className="rounded-xl bg-[#F5ECD7] p-4 text-sm text-slate-700" data-testid="jobs-member-note"><Users className="inline h-4 w-4 mr-1 text-[#F08A3C]" />Full job details and applying are for approved <Link to="/community" className="font-semibold text-[#147C88]">community members</Link>. Joining is free.</p>}
      {data.featured?.length > 0 && (
        <div className="rounded-3xl bg-[#0B2A4A] p-5 sm:p-6" data-testid="jobs-featured">
          <p className="font-mono-ui text-xs tracking-[0.3em] text-[#F08A3C] flex items-center gap-2"><Star className="h-4 w-4" /> TOP JOBS</p>
          <div className="mt-4 grid md:grid-cols-3 gap-4">{data.featured.map((j) => <JobCard key={j.id} j={j} featured />)}</div>
        </div>
      )}
      {data.jobs.length ? <div className="grid md:grid-cols-2 gap-4" data-testid="jobs-list">{data.jobs.map((j) => <JobCard key={j.id} j={j} />)}</div> : !data.featured?.length && <p className="text-slate-600" data-testid="jobs-empty">No open jobs right now. Check back soon, or post one.</p>}
    </div>
  );
}

export default function JobsPage() {
  useSeo(SEO);
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "browse";
  const [data, setData] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => { if (user !== null) api.get("/jobs").then((r) => setData(r.data)).catch(() => setData({ jobs: [], is_member: false })); }, [user]);
  const setTab = (t) => setParams(t === "browse" ? {} : { tab: t });
  return (
    <article data-testid="jobs-page">
      <PageHero eyebrow="DIGITAL NOMAD JOBS · WORK FROM ANYWHERE" title="Remote jobs for digital nomads"
        subtitle="Employers from anywhere post remote and location-flexible jobs; digital nomads from anywhere apply. Every job is reviewed by Greg before it goes live. We just help make the connection.">
        <button onClick={() => setTab("post")} className="inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-6 py-3 font-semibold hover:bg-[#D96F28]" data-testid="jobs-post-cta"><Plus className="h-4 w-4" /> Post a job (free)</button>
        <Link to="/community" className="inline-flex items-center gap-2 rounded-xl bg-white/10 border border-white/25 text-white px-6 py-3 font-semibold" data-testid="jobs-community-cta">Join the community</Link>
      </PageHero>
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#0B2A4A]/5 max-w-full overflow-x-auto justify-start">
            <TabsTrigger value="browse" data-testid="jobs-tab-browse">Browse jobs</TabsTrigger>
            <TabsTrigger value="post" data-testid="jobs-tab-post">Post a job</TabsTrigger>
            <TabsTrigger value="posts" data-testid="jobs-tab-posts">My job posts</TabsTrigger>
            <TabsTrigger value="applied" data-testid="jobs-tab-applied">My applications</TabsTrigger>
          </TabsList>
          <TabsContent value="browse" className="mt-6"><Browse data={data} /></TabsContent>
          <TabsContent value="post" className="mt-6">{user ? <JobForm onDone={() => { setReloadKey((k) => k + 1); setTab("posts"); }} /> : <SignInPrompt what="post a job" />}</TabsContent>
          <TabsContent value="posts" className="mt-6">{user ? <MyPosts reloadKey={reloadKey} /> : <SignInPrompt what="see your job posts and their applications" />}</TabsContent>
          <TabsContent value="applied" className="mt-6">{user ? <MyApplied /> : <SignInPrompt what="see your applications" />}</TabsContent>
        </Tabs>
      </section>
    </article>
  );
}
